import { useUser } from "contexts/UserContext";
import {
  hasPermission,
  getModulePermissions,
  hasAnyModulePermission,
  getAccessibleModules,
  isAdmin,
} from "utils/permissions";

/**
 * Custom hook for checking permissions in components
 * Usage: const { can, canAny, cannot, permissions } = usePermission()
 * 
 * Examples:
 * - if (can('lead_management', 'create')) → show create button
 * - if (cannot('staff_management', 'delete')) → disable delete button
 * - if (canAny('lead_management')) → show module section
 */
const DEFAULT_ACCESSIBLE_MODULES = [
  "dashboard",
  "calendar",
  "careof_management",
  "lead_management",
  "client_management",
  "staff_management",
  "lead_folder",
];

export const usePermission = () => {
  const { user } = useUser();
  const permissions = user?.permissions || {};
  const isBusinessman = user?.role?.toUpperCase() === "BUSINESSMAN";

  /**
   * Check if user has specific permission
   * @param {string} module - Module name
   * @param {string} action - Action ('read', 'create', 'update', 'delete')
   * @returns {boolean}
   */
  const can = (module, action) => {
    if (isBusinessman) return true;
    return hasPermission(permissions, module, action);
  };

  /**
   * Check if user does NOT have permission (inverted)
   * @param {string} module - Module name
   * @param {string} action - Action
   * @returns {boolean}
   */
  const cannot = (module, action) => {
    return !can(module, action);
  };

  /**
   * Check if user has ANY permission in a module
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const canAny = (module) => {
    if (isBusinessman) return true;
    return hasAnyModulePermission(permissions, module);
  };

  /**
   * Get all permissions for a module
   * @param {string} module - Module name
   * @returns {Object} { read, create, update, delete }
   */
  const getModule = (module) => {
    if (isBusinessman) {
      return { read: true, create: true, update: true, delete: true };
    }
    return getModulePermissions(permissions, module);
  };

  /**
   * Get list of modules user has access to
   * @returns {Array} Module names
   */
  const getAccessible = () => {
    if (isBusinessman) {
      return Array.from(new Set([...Object.keys(permissions), ...DEFAULT_ACCESSIBLE_MODULES]));
    }
    return getAccessibleModules(permissions);
  };

  /**
   * Check if user is admin (typically has staff_management permission)
   * @returns {boolean}
   */
  const isAdminUser = () => {
    if (isBusinessman) return true;
    return isAdmin(permissions);
  };

  return {
    can,
    cannot,
    canAny,
    getModule,
    getAccessible,
    isAdminUser,
    permissions,
  };
};

export default usePermission;
